import {Resource} from "../../resources/common"
import {Dispute, getSourceDisplay, StatusTypeValue} from "../../resources/dispute"
import React, {ReactElement} from "react"
import {AllowedDisputesColumns, DisputesColumns} from "./Disputes.types"
import {useNavigate} from "react-router-dom"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {OrgName} from "../OrgName/OrgName"
import numeral from "numeral"
import IdLink from "../IdLink/IdLink"
import moment from "moment"
import {kebabCase} from "lodash"

export default function DisputeRow({
	dispute,
	include,
	includedColumns,
}: {
	dispute: Dispute
	include?: Resource[]
	showCustomerId?: boolean
	includedColumns: Array<AllowedDisputesColumns>
}) {
	const navigate = useNavigate()
	const navigateWithListPath = useNavigateWithListPath()
	const customerId = dispute.relationships.customer.data.id
	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined
	const updatedAt = dispute.attributes.updatedAt
	const status = dispute.attributes.statusHistory.slice(-1)[0].type
	const org = dispute.relationships.org?.data.id

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedDisputesColumns, ReactElement>> = {}

	if (includedColumns.includes(DisputesColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className="data-table-id" href={`/disputes/${dispute.id}`}>
					{dispute.id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(DisputesColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(DisputesColumns.source)) {
		contentColumns["Source"] = <DataTableCell>{getSourceDisplay(dispute, include)}</DataTableCell>
	}

	if (includedColumns.includes(DisputesColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>{numeral(dispute.attributes.amount / 100).format("$0,0.00")}</DataTableCell>
		)
	}

	if (includedColumns.includes(DisputesColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(dispute.attributes.createdAt).format("L")}</DataTableCell>
	}

	if (includedColumns.includes(DisputesColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("disputes-status", kebabCase(status))}>{StatusTypeValue(status)}</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(DisputesColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>
				{updatedAt ? moment(updatedAt).format("L") : <span className="empty-date-placeholder"> --/--/---- </span>}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(DisputesColumns.customerName)) {
		contentColumns["Customer Name"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(DisputesColumns.customerId)) {
		contentColumns["Customer Id"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{dispute.relationships.customer.data.id}
				</a>
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/disputes/${dispute.id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}
